import React, { useState, useEffect } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import LoadingComponent from '../../common/LoadingComponent';
import Header from './Header';
import DataToolbar from './DataToolbar';
import FiltersDrawer from './FiltersDrawer';
import AnalyzeMap from './AnalyzeMap';
import TaskGrid from './TaskGrid';
import SegmentGrid from './SegmentsGrid';
import '../Dashboard/styles.scss';
import {
    getProjectLocations,
    getLittersForManySegments,
    getResearchProjects,
    getAllResearchLocations,
    getProjectZones,
    updateResearchTaskStatus,
    updateResearchSegmentStatus,
} from '../../api';
import {
    formatRawTasks,
    formatRawSegments,
    lineStringToLatLng,
    countAccepted,
} from './transformers';
import { getClassificationByImageType } from '../Segments/SegmentPhotos/constants';
import './styles.scss';

const DEFAULT_REQUEST_LIMIT = 10;

/**
 * Analyze Page Component
 */
export default function Analyze() {
    console.log('Rendering Analyze component...');

    const fullScreenHandler = useFullScreenHandle();

    // Loading state
    const [isLoading, setIsLoading] = useState(false);

    // Header state
    const [statusDataSource, setStatusDataSource] = useState('Task');
    const [projectStatusNumAccepted, setProjectStatusNumAccepted] = useState(0);
    const [projectStatusTotal, setProjectStatusTotal] = useState(0);

    // Project selector state
    const [allProjects, setAllProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);

    // Grid state (counts/pages for TaskGrid only, since SegmentsGrid doesn't page)
    const [rowCount, setRowCount] = useState(); // set equal to total Tasks in selected Project
    const [totalAcceptedTaskCount, setTotalAcceptedTaskCount] = useState(0);
    const [totalTaskCount, setTotalTaskCount] = useState(0);
    const [editMode, setEditMode] = useState(false); // Used by Grid while showing both Tasks and Segments
    const [taskSelectionModel, setTaskSelectionModel] = useState([]);
    const [segmentSelectionModel, setSegmentSelectionModel] = useState([]);

    // State used for the Tasks query!
    // When any of this state changes, will re-fetch the tasks!
    // - page, pageSize, sortOrder, sortField, filter status, filter zones, filter locations
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sortOrder, setSortOrder] = useState();
    const [sortField, setSortField] = useState();
    const [taskStatusState, setTaskStatusState] = useState({
        UNASSIGNED: false,
        ASSIGNED: false,
        SUBMITTED: false,
        IN_PROGRESS: false,
        SKIPPED: false,
        ACCEPTED: false,
        REJECTED: false,
    });
    const [zoneIdFilter, setzoneIdFilter] = useState([]);
    const [locationIdFilter, setlocationIdFilter] = useState([]);

    // Tasks state
    const [fetchedTasks, setFetchedTasks] = useState([]); // list of Tasks from fetchProjectTasks call
    const [formattedTasks, setFormattedTasks] = useState([]); // Tasks ready to display in TaskGrid
    const [taskMapPoints, setTaskMapPoints] = useState([]); // Tasks ready to display in AnalyzeMap
    const [selectedTask, setSelectedTask] = useState(null); // Used to display Header bar and Task name in breadcrumb
    // Segments state
    const [formattedSegments, setFormattedSegments] = useState([]); // Segments ready to display in SegmentsGrid
    const [segmentMapPoints, setSegmentMapPoints] = useState([]); // Segments ready to display in AnalyzeMap
    // Litters state: mapping from segmentId to array of litters in the Segment (including litters, bins, and hotspots)
    const [littersBySegmentId, setLittersBySegmentId] = useState({});

    // Showing Tasks vs Segments state - these 2 state and 1 const could be combined into one! (and Header's setStatusDataSource as well)
    const [isShowingTasksMap, showTasksMap] = useState(true);
    const [isShowingSegmentsMap, showSegmentsMap] = useState(false);
    const shouldShowSegmentsGrid =
        isShowingSegmentsMap &&
        taskSelectionModel &&
        taskSelectionModel.length > 0;

    // Filter drawer state
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    // Filter: location
    const [allLocationsForFilter, setAllLocationsForFilter] = useState([]); // locations used filter drawer
    const [allLocationFilterCheckbox, setAllLocationFilterCheckbox] = useState(
        true,
    );
    // - Filter: zone
    const [allZonesInProject, setAllZonesInProject] = useState([]);
    const [allZonesFilterCheckbox, setAllZonesFilterCheckbox] = useState(true);

    // Set the Header data for Task completion percentage
    const setStatusBarForTasks = (acceptedCount, totalCount) => {
        setStatusDataSource('Task');
        setProjectStatusNumAccepted(acceptedCount);
        setProjectStatusTotal(totalCount);
    };

    // Set the Header data for Segment completion percentage
    // TODO: Make this update when user changes a Segment status!
    const setStatusBarForSegments = (segmentData, selectedTask) => {
        const totalRequiredSegments = selectedTask.taskTypeNumSegments;
        const numAccepted = countAccepted(segmentData);

        setStatusDataSource('Segment');
        setProjectStatusNumAccepted(numAccepted);
        setProjectStatusTotal(totalRequiredSegments);
    };

    const refresh = async () => {
        // console.log('REFRESHING TASKS (including their SEGMENTS)');
        setIsLoading(true);
        await performFilteredFetchTasks();
        setIsLoading(false);
    };

    // Initial call to fetch all Research Projects for the dropdown
    useEffect(() => {
        if (!allProjects.length) fetchAllResearchProjects();
    }, []);

    // Every time the TaskGrid paging, sort or filter params change, refresh the data!
    useEffect(refresh, [
        selectedProject,
        page,
        pageSize,
        sortOrder,
        sortField,
        taskStatusState,
        zoneIdFilter,
        locationIdFilter,
    ]);

    /**
     * Fetch all Projects for dropdown menu
     * sets default project to first item in array
     * Fetches all Zones and Locations for default project
     * Fetches all Tasks for default project to populate Map and Grid
     */
    const fetchAllResearchProjects = async () => {
        console.log('fetchAllResearchProjects');
        // setIsLoading(true);
        const result = await getResearchProjects();
        // setIsLoading(false);
        if (result) {
            setAllProjects(result);
            // set the first item of the project if exists

            if (result[0]) {
                setSelectedProject(result[0]);

                // Fetch all Project filtering options for Zones and Locations of this Project/Partner
                fetchAllProjectZonesAndLocations(result[0].id);

                // Fetch Tasks in Project (paginated)
                await fetchProjectTasks(result[0].id);
            }

            return result;
        }
    };

    /**
     * Handles Project dropdown selection
     *
     * @param {object} selected - selected Project object
     */
    const handleSelectProject = async (selected) => {
        console.log('handleSelectProject', selected);

        setSelectedProject(selected);
        clearTaskSelection();
        clearFilterSelection();

        if (selected && !isNaN(selected.id)) {
            // Fetch all Project filtering options for Zones and Locations of this Project/Partner
            // TODO: Optimize by not re-querying Locations, which are at the Partner level, not Project
            fetchAllProjectZonesAndLocations(selected.id);
        }
    };

    /**
     * Fetch all Project filtering options for Zones and Locations of this Project/Partner
     * @param {*} projectId
     */
    const fetchAllProjectZonesAndLocations = async (projectId) => {
        // Fetch Zones for filter
        const zones = await getProjectZones(projectId);
        if (zones) setAllZonesInProject(zones);

        // Fetch Locations for filter
        const listNamesOnly = true;
        const locations = await getAllResearchLocations(listNamesOnly);
        if (locations) {
            // Pass an array of locations [{id: number, name: string}]
            setAllLocationsForFilter(locations);
        }
    };

    /**
     * Fetch Tasks for a Project (paginated)
     *
     * @param {number} - projectId
     * @param {number} - skip
     * @param {number} - limit
     * @param {string} - sortOrder
     * @param {string} - sortField
     * @param {array} - statuses
     * @param {array} - zoneIds
     * @param {array} - locationIds
     */
    const fetchProjectTasks = async (
        selectedProjectId,
        skip,
        limit,
        sortOrder,
        sortField,
        statuses,
        zoneIds,
        locationIds,
    ) => {
        console.log(
            'fetchProjectTasks',
            selectedProjectId,
            skip,
            limit,
            sortOrder,
            sortField,
            statuses,
            zoneIds,
            locationIds,
        );
        const taskResult = await getProjectLocations(
            selectedProjectId,
            skip,
            limit,
            sortOrder,
            sortField,
            statuses,
            zoneIds,
            locationIds,
        );

        if (taskResult && taskResult.data) {
            const tasks = taskResult.data;
            const totalAcceptedTasks = taskResult.meta.totalAccepted;
            const totalTasks = taskResult.meta.total;

            console.log(
                'fetchProjectTasks success',
                tasks,
                taskResult.meta.total,
            );

            setFetchedTasks(tasks);
            setRowCount(totalTasks);
            setTotalTaskCount(totalTasks);
            setTotalAcceptedTaskCount(totalAcceptedTaskCount);
            // Update the Header ProjectStatus
            setStatusBarForTasks(totalAcceptedTasks, totalTasks);

            handleFetchedTasks(tasks);
            return tasks;
        }
    };

    /**
     * Formats the newly fetched Tasks for the Map and TaskGrid
     *
     * @param {Array<Task>} tasks- array of Tasks fetched from API
     */
    const handleFetchedTasks = (tasks) => {
        console.log('handleFetchedTasks');
        formatTasksForDataGrid(tasks);

        const taskPoints = calculateMapPointsForTasks(tasks);
        console.log(
            'handleFetchedTasks: calculateMapPointsForTasks returned taskPoints: ',
            taskPoints,
        );
        setTaskMapPoints(taskPoints);

        // Don't format Segments now after fetching Tasks.  Wait until a Task is selected.
        // formatSegmentsForDataGrid(tasks);
    };

    /**
     * Calls the transformer formatRawTasks, which counts up numbers of total, accepted and rejected Segments for each Task.
     * Those results are displayed in the TaskGrid columns "Accepted Segments" and "Rejected Segments".
     *
     * @param {Array<Task>} tasks
     */
    const formatTasksForDataGrid = (tasks) => {
        const tasksData = formatRawTasks(tasks);
        setFormattedTasks(tasksData);
    };

    /**
     * For the specified Task list, prepare the Segment list for the SegmentsGrid.
     * Although this accepts a Task list, it is currently only called when a single Task is clicked,
     * passing in that ONE task to this function.
     * @param {Array<Task>} tasks
     * @returns
     */
    const formatSegmentsForDataGrid = (tasks) => {
        const formattedSegmentsForTasks = formatRawSegments(tasks);
        // Setting state will cause re-render but new value isn't used during this current render, so also must return it
        setFormattedSegments(formattedSegmentsForTasks);

        // Fetch all litters of all Segments in the given Task(s)
        // LT-7361 Should fetch litters in order to show photo count columns, but shouldn't block showing other data while we wait
        // LT-7369 LT-7370 Actually, only fetch for SegmentsGrid because we're removing those columns from TaskGrid
        // const formattedSegmentIds = formattedSegmentsForTasks.map(
        //     (segment) => segment.id,
        // );
        // if (formattedSegmentIds.length)
        //     fetchSegmentLitters(formattedSegmentIds);

        return formattedSegmentsForTasks;
    };

    /**
     * Handle TaskGrid selection
     * @param {number[]} selections - array with selected Task's id, provided by DataGrid
     */
    const handleSelectTask = async (selections) => {
        console.log('handleSelectTask', selections);
        setTaskSelectionModel(selections);

        if (!editMode) {
            showSegmentsMap(true);
            showTasksMap(false);

            // Set all Segments of the selected Task as the formattedSegments
            // These are used as the rows of the SegmentsGrid
            const selectedTaskData = fetchedTasks.find(
                (t) => t.id === selections[0],
            );
            setSelectedTask(selectedTaskData); // Used to help display Header bar and Task name in breadcrumb

            if (selectedTaskData) {
                // Fetch the Litters for all Segments of the selected Task
                // Augments each Task with its count of photos by litter type
                // TODO: Optimization: Don't fetch again if we already fetched the litters for these same segmentIds
                const segmentIds = selectedTaskData.activities.map(
                    (segment) => segment.id,
                );
                const littersBySegmentIdData = await fetchSegmentLitters(
                    segmentIds,
                );

                // Call the existing function that transforms the Task's activities into the correct Segment format for the SegmentGrid
                const segmentsForSelectedTask = formatSegmentsForDataGrid([
                    selectedTaskData,
                ]);
                console.log(
                    'handleSelectTask: after calling formatSegmentsForDataGrid: ',
                    segmentsForSelectedTask,
                );

                // calculate segment % completion for header
                setStatusBarForSegments(
                    segmentsForSelectedTask,
                    selectedTaskData,
                );

                // Now that formattedSegments is populated, augment the Segment data for the map
                const segmentPoints = calculateMapPointsForSegmentsOfTask(
                    selectedTaskData,
                    littersBySegmentIdData,
                );
                console.log(
                    'handleSelectTask: calculateMapPointsForSegmentsOfTask returned segmentPoints: ',
                    segmentPoints,
                );

                setSegmentMapPoints(segmentPoints);
            } else {
                console.error(
                    'handleSelectTask: Cannot find the Task selected in:',
                    fetchedTasks,
                );
            }
        }
    };

    const handleBulkTaskStatusChange = (status) => {
        console.log('#### handleBulkTaskStatusChange', status);

        // All selected segments
        console.log(
            '#### handleBulkTaskStatusChange: All selected tasks',
            taskSelectionModel,
        );

        // call API to update Task status
        for (const taskId of taskSelectionModel) {
            updateResearchTaskStatus(taskId, status);
        }

        // TODO: Move this mapping inside the setter
        // Update ALL Task status in the state
        const taskDataWithNewStatus = formattedTasks.map((task) => {
            if (taskSelectionModel.includes(task.id)) {
                return { ...task, status };
            } else {
                return task;
            }
        });
        setFormattedTasks(taskDataWithNewStatus);
    };

    // Unused, the Accept/Reject All Checked buttons are hidden for Segments
    const handleBulkSegmentStatusChange = (status) => {
        console.log('#### handleBulkSegmentStatusChange', status);

        // All selected segments
        console.log(
            '#### handleBulkSegmentStatusChange: All selected segments',
            segmentSelectionModel,
        );

        // call API to update Segment status
        for (const segmentId of segmentSelectionModel) {
            updateResearchSegmentStatus(segmentId, status);
        }

        // TODO: Move this mapping inside the setter
        // Update ALL Segment status in the state
        const segmentDataWithNewStatus = formattedSegments.map((segment) => {
            if (segmentSelectionModel.includes(segment.id)) {
                return { ...segment, status };
            } else {
                return segment;
            }
        });
        setFormattedSegments(segmentDataWithNewStatus);
    };

    /**
     * Fetch litters for an array of segment Ids.
     * Used to get summary photo count for one Task or all visible Tasks.
     *
     * @param {number[]} querySegmentIds
     */
    const fetchSegmentLitters = async (querySegmentIds) => {
        if (querySegmentIds && querySegmentIds.length > 0) {
            setIsLoading(true);
            const segmentLitters = await getLittersForManySegments({
                segmentIds: querySegmentIds.join(),
            });
            setIsLoading(false);

            // check if object has values before setting
            if (segmentLitters && Object.keys(segmentLitters).length > 0) {
                // append new data to litters store
                const newLittersObject = {
                    ...littersBySegmentId,
                    ...segmentLitters,
                };
                setLittersBySegmentId(newLittersObject);

                return newLittersObject;
            }
        }
        // Return the current litters from state without these latest updates
        return littersBySegmentId;
    };

    /**
     * Calculate all Map points for the Segments of the specified Task.
     *
     * Map point objects for Tasks with no Activities are Task's Location (failing back to the position 0,0)
     * Map point objects for Activities with "location" polylines have location array, and an array of markerDataPoints for each Litter
     *
     * @param {*} selectedTask
     * @returns array of Map point objects
     */
    const calculateMapPointsForSegmentsOfTask = (
        selectedTask,
        littersBySegmentIdData,
    ) => {
        console.log(
            'calculateMapPointsForSegmentsOfTask',
            selectedTask.activities,
            littersBySegmentIdData,
        );

        // If Task has no Activities
        if (!selectedTask.activities || selectedTask.activities.length === 0) {
            if (
                (selectedTask.location &&
                    !selectedTask.location.startLocationParsed.lat) ||
                (selectedTask.location &&
                    !selectedTask.location.startLocationParsed.long)
            ) {
                // Task's Location has no lat/long data
                return [{ position: { lat: 0, lng: 0 } }];
            } else {
                // Use the Task's Location's lat/long
                return [
                    {
                        classification: 'LOCATION',
                        position: {
                            lat: selectedTask.location.startLocationParsed.lat,
                            lng: selectedTask.location.startLocationParsed.long,
                        },
                    },
                ];
            }
        } else {
            // Task has Activities!
            // Build an object for each Segment with its location and marker for every litter location
            return selectedTask.activities.map((segment) => {
                let formatted = {
                    location:
                        segment.locationParsed &&
                        segment.locationParsed.coordinates
                            ? lineStringToLatLng(
                                  segment.locationParsed.coordinates,
                              )
                            : [],
                    markerDataPoints: [],
                    segmentId: segment.id, // Used to know which points to mark checked when a Segment in the Grid is checked
                    isChecked: false, // Mark this point checked when its Segment in the Grid is checked
                };

                for (const litter of littersBySegmentIdData[segment.id]) {
                    formatted.markerDataPoints.push({
                        classification: litter.classification
                            ? litter.classification
                            : getClassificationByImageType(litter.imageType), // Support new litterati-research uploads without classification by determining classification by imageType
                        photoUrl: litter.file,
                        position: {
                            lng: litter.location.coordinates[0],
                            lat: litter.location.coordinates[1],
                        },
                    });
                }

                return formatted;
            });
        }
    };

    /**
     * Calculate all Map points for the specified Tasks
     * @param {*} tasks All Tasks visible in the TaskGrid which need Map points
     * @returns array of Map points
     */
    const calculateMapPointsForTasks = (tasks) => {
        console.log('calculateMapPointsForTasks', tasks);

        // show formatted Task data
        return tasks.map((t) => {
            if (t.location) {
                if (t.location && t.location.startLocationParsed) {
                    if (
                        !t.location.startLocationParsed.lat ||
                        !t.location.startLocationParsed.long
                    ) {
                        return { position: { lat: 0, lng: 0 } };
                    } else {
                        return {
                            classification: 'LOCATION',
                            position: {
                                lat: t.location.startLocationParsed.lat,
                                lng: t.location.startLocationParsed.long,
                            },
                        };
                    }
                }
            } else {
                return { position: { lat: 0, lng: 0 } };
            }
        });
    };

    /**
     * Clear Task Selection when:
     * - user clicks breadcrumb home "Tasks >"
     * - user selects a Project from the dropdown
     */
    const clearTaskSelection = async () => {
        // Clear the Grid
        setTaskSelectionModel([]);
        setEditMode(false);

        // Clear the Map
        showTasksMap(true);
        showSegmentsMap(false);
        setSegmentMapPoints([]);

        // Clear the Header
        setStatusBarForTasks(totalAcceptedTaskCount, totalTaskCount);
    };

    /**
     * Handle SegmentsGrid selection
     * @param {number[]} selections - array with selected Segment ids, provided by DataGrid
     */
    const handleSelectSegment = async (selections) => {
        console.log('handleSelectSegment', selections, segmentMapPoints);
        setSegmentSelectionModel(selections);

        // Mark the Map points for the selections as isChecked,
        // so any Segment points which are not checked can be filtered out from the Map when others are checked.
        for (const point of segmentMapPoints) {
            if (selections.includes(point.segmentId)) {
                // Segment is checked in the Grid
                point.isChecked = true;
                // console.log('Checking point for segment', point.segmentId);
            } else {
                // Segment is not checked in the Grid
                point.isChecked = false;
            }
        }
        // Thankfully, we don't seem to need to call setSegmentMapPoints to update the state
    };

    ///////////// FILTERS

    /**
     * Clear the selections and checks All Zones and All Locations.
     *  - When the Project selection changes
     *      Because, Zones and Locations may be tied to a Project.
     * Does NOT reset the Status filtering selections.
     */
    const clearFilterSelection = () => {
        setAllLocationFilterCheckbox(true);
        setlocationIdFilter([]);
        setAllZonesFilterCheckbox(true);
        setzoneIdFilter([]);
    };

    /**
     * Handle "All Locations" checkbox input
     *
     * @param {Event} event - HTML checkbox input event
     */
    const handleAllLocationFilterChange = (event) => {
        const checked = event.target.checked;
        console.log('handleAllLocationFilterChange', checked);

        // set the boolean state of the checkbox to the opposite value
        setAllLocationFilterCheckbox(!allLocationFilterCheckbox);

        // Whether checking or unchecking, clear the selected locationIds and don't pass any as query filters
        setlocationIdFilter([]);
    };

    /**
     * Handle Location filter selection event
     *
     * @param {Event} event - HTML event whose target is the HTML Select
     */
    const handleFilterLocationsChange = (event) => {
        console.log('handleFilterLocationsChange', event.target.value);

        const selectedValues = [];

        for (const selectedOption of event.target.selectedOptions) {
            selectedValues.push(parseInt(selectedOption.value));
        }
        setlocationIdFilter(selectedValues);
    };

    /**
     * Handle "All Zones" checkbox input
     *
     * @param {Event} event - HTML checkbox input event
     */
    const handleAllZonesFilterChange = (event) => {
        const checked = event.target.checked;
        console.log('handleAllZonesFilterChange', checked);

        // set the boolean state of the checkbox to the opposite value
        setAllZonesFilterCheckbox(!allZonesFilterCheckbox);

        // Whether checking or unchecking, clear the selected zoneIds and don't pass any as query filters
        setzoneIdFilter([]);
    };

    /**
     * Handle Zone filter selection event
     *
     * @param {Event} event - HTML event whose target is the HTML Select
     */
    const handleFilterZonesChange = (event) => {
        console.log('handleFilterZonesChange', event.target.value);

        let selectedValues = [];

        for (const selectedOption of event.target.selectedOptions) {
            selectedValues.push(parseInt(selectedOption.value));
        }
        setzoneIdFilter(selectedValues);
    };

    /**
     * Handle filter selection event for Task status.
     * Update the filter state and perform a new fetch.
     *
     * @param {string} target - Task status to add to the filter
     */
    const handleTaskStatusFilterChange = (target) => {
        console.log(
            'handleTaskStatusFilterChange',
            target,
            // JSON.stringify(taskStatusState),
        );

        // Change taskStatusState to include the new status value
        // taskStatusState[target] = !taskStatusState[target]; // BAD state code, must use a setter, even for an object
        setTaskStatusState((currentTaskStatusState) => {
            const newObj = { ...currentTaskStatusState };
            newObj[target] = !currentTaskStatusState[target];
            console.log(
                'handleTaskStatusFilterChange: new state',
                JSON.stringify(newObj),
            );
            return newObj;
        });
    };

    /**
     * Fetch Tasks, using all filter, sort and paging values in the state
     */
    const performFilteredFetchTasks = () => {
        console.log(
            'performFilteredFetchTasks: status filters',
            JSON.stringify(taskStatusState),
        );
        let statuses;
        let locationIds;
        let zoneIds;

        // Task status determination
        if (Object.values(taskStatusState).includes(true)) {
            statuses = Object.keys(taskStatusState).filter(
                (statusName) => taskStatusState[statusName] === true,
            );

            console.log('performFilteredFetchTasks statuses', statuses);
        }

        // Location filter
        if (locationIdFilter.length > 0) locationIds = locationIdFilter;

        // Zone filter
        if (zoneIdFilter.length > 0) zoneIds = zoneIdFilter;

        if (selectedProject && !isNaN(selectedProject.id)) {
            const skip = page * pageSize;

            fetchProjectTasks(
                selectedProject.id,
                skip,
                pageSize ? pageSize : DEFAULT_REQUEST_LIMIT,
                sortOrder,
                sortField,
                statuses,
                zoneIds,
                locationIds,
            );
        }
    };

    ///////////// END FILTERS

    return (
        <div className="col-12">
            <FullScreen handle={fullScreenHandler}>
                <div className="fullScreenView">
                    <div className="row mt-2">
                        <Header
                            allProjects={allProjects}
                            selectedProject={selectedProject}
                            handleSelectProject={handleSelectProject}
                            statusDataSource={statusDataSource}
                            numAccepted={projectStatusNumAccepted}
                            total={projectStatusTotal}
                            fullScreenHandler={fullScreenHandler}
                        />
                    </div>
                    <div>
                        <div className="row">
                            {isShowingTasksMap && (
                                <AnalyzeMap
                                    zoomLevel={18}
                                    dataPoints={taskMapPoints}
                                />
                            )}
                            {isShowingSegmentsMap && (
                                // If any Segments are checked, show only those.  Otherwise, show all.
                                <AnalyzeMap
                                    zoomLevel={18}
                                    dataPoints={
                                        segmentMapPoints.some(
                                            (point) => point.isChecked,
                                        )
                                            ? segmentMapPoints.filter(
                                                  (point) => point.isChecked,
                                              )
                                            : segmentMapPoints
                                    }
                                />
                            )}
                        </div>
                    </div>
                    <div className="spacer"></div>
                    <div className="row mb-2">
                        <DataToolbar
                            shouldShowSegments={shouldShowSegmentsGrid}
                            editMode={editMode}
                            setEditMode={setEditMode}
                            handleBulkTaskStatusChange={
                                handleBulkTaskStatusChange
                            }
                            handleBulkSegmentStatusChange={
                                // Unused, buttons hidden for Segments
                                handleBulkSegmentStatusChange
                            }
                            selectedTaskName={selectedTask && selectedTask.name}
                            refresh={refresh}
                            showFilters={() => setFilterDrawerOpen(true)}
                            handleHomeClick={clearTaskSelection}
                        />
                    </div>
                    {isLoading ? (
                        <div className="row analyzeLoader">
                            <LoadingComponent isLoading={isLoading} />
                        </div>
                    ) : (
                        <>
                            <div className="row mb-2">
                                {shouldShowSegmentsGrid ? (
                                    <SegmentGrid
                                        segmentData={formattedSegments}
                                        setSegmentData={setFormattedSegments}
                                        editMode={editMode}
                                        littersBySegmentId={littersBySegmentId}
                                        setStatusBarForSegments={
                                            // Unused
                                            setStatusBarForSegments
                                        }
                                        selectionModel={segmentSelectionModel}
                                        onSelect={handleSelectSegment}
                                    />
                                ) : (
                                    <TaskGrid
                                        taskData={formattedTasks}
                                        setTaskData={setFormattedTasks}
                                        // fetchProjectTasks={fetchProjectTasks} // Deprecated, instead setters will change state which triggers useEffect to call performFilteredFetchTasks
                                        // selectedProject={selectedProject}
                                        editMode={editMode}
                                        page={page}
                                        setPage={setPage}
                                        pageSize={pageSize}
                                        setPageSize={setPageSize}
                                        setSortOrder={setSortOrder}
                                        setSortField={setSortField}
                                        rowCount={rowCount}
                                        selectionModel={taskSelectionModel}
                                        onSelect={handleSelectTask}
                                    />
                                )}
                            </div>
                            {filterDrawerOpen && (
                                <FiltersDrawer
                                    allLocationsForFilter={
                                        allLocationsForFilter
                                    }
                                    allZones={allZonesInProject}
                                    zoneState={{
                                        allZoneFilterChecked: allZonesFilterCheckbox,
                                        handleAllZoneFilterChange: handleAllZonesFilterChange,
                                        filterZoneValue: zoneIdFilter,
                                        handleFilterZonesChange: handleFilterZonesChange,
                                    }}
                                    locationState={{
                                        allLocationFilterChecked: allLocationFilterCheckbox,
                                        handleAllLocationFilterChange: handleAllLocationFilterChange,
                                        filterLocationsValue: locationIdFilter,
                                        handleFilterLocationsChange: handleFilterLocationsChange,
                                    }}
                                    {...taskStatusState}
                                    handleTaskStatusFilterChange={
                                        handleTaskStatusFilterChange
                                    }
                                    filterDrawerOpen={filterDrawerOpen}
                                    hideFilters={() =>
                                        setFilterDrawerOpen(false)
                                    }
                                />
                            )}
                        </>
                    )}
                </div>
            </FullScreen>
        </div>
    );
}
